import React, { useState, useEffect } from 'react';
import { TomeButton } from '../../primitives/tome';
import { storage } from '../../../services/StorageService';
import { SettingsToggle } from './components/SettingsToggle';
import {
  isTouchControlsEnabled,
  setTouchControlsPreference,
} from '../../mobile/touchControlsFlag';
import { keyBindingsNavigationStore } from '../KeyBindings/keyBindingsNavigationStore';

export interface ControlsTabProps {
  /** Optional explicit bar list override for backwards compatibility. */
  activeBarIds?: readonly number[];
}

/**
 * ControlsTab — Gameplay control switches (Facing direction only, Virtual Touch
 * Controls) and the launcher button for the dedicated Key Bindings TomeWindow
 * (Stage 2 / Round 5A / Unification Plan).
 */
export const ControlsTab: React.FC<ControlsTabProps> = () => {
  const [facingDirectionOnly, setFacingDirectionOnly] = useState(
    () => storage.loadUserPrefs().controls.facingDirectionOnly,
  );

  useEffect(() => {
    const unsub = storage.subscribeUserPrefs((p) => {
      setFacingDirectionOnly(p.controls.facingDirectionOnly);
    });
    return () => {
      unsub();
    };
  }, []);

  const toggleFacingDirectionOnly = () => {
    const cur = storage.loadUserPrefs();
    storage.saveUserPrefs({
      ...cur,
      controls: {
        ...cur.controls,
        facingDirectionOnly: !cur.controls.facingDirectionOnly,
      },
    });
  };

  const [touchControlsEnabled, setTouchControlsEnabled] = useState<boolean>(
    () => isTouchControlsEnabled(),
  );

  const toggleTouchControls = () => {
    const next = !touchControlsEnabled;
    setTouchControlsEnabled(next);
    setTouchControlsPreference(next ? 'on' : 'off');
  };

  return (
    <>
      <section className="settings-section" aria-labelledby="settings-aiming-heading">
        <h3 className="settings-section-title" id="settings-aiming-heading">Aiming</h3>
        <div className="settings-card">
          <div className="settings-card-row">
            <div>
              <label
                htmlFor="settings-facing-direction-only"
                className="settings-card-label"
                style={{ cursor: 'pointer' }}
              >
                Facing direction only
              </label>
              <div className="settings-hint" style={{ margin: '2px 0 0' }}>
                Free aim fires horizontally in the direction your character faces.
              </div>
            </div>
            <SettingsToggle
              id="settings-facing-direction-only"
              checked={facingDirectionOnly}
              onChange={toggleFacingDirectionOnly}
              data-testid="settings-facing-direction-only"
              aria-label="Facing direction only"
            />
          </div>
        </div>
      </section>

      <section className="settings-section" aria-labelledby="settings-touch-heading">
        <h3 className="settings-section-title" id="settings-touch-heading">Mobile & Touch Controls</h3>
        <div className="settings-card">
          <div className="settings-card-row">
            <div>
              <label
                htmlFor="settings-touch-controls"
                className="settings-card-label"
                style={{ cursor: 'pointer' }}
              >
                Virtual Touch Controls
              </label>
              <div className="settings-hint" style={{ margin: '2px 0 0' }}>
                On-screen movement joystick and action cluster for mobile and touchscreen play.
              </div>
            </div>
            <SettingsToggle
              id="settings-touch-controls"
              checked={touchControlsEnabled}
              onChange={toggleTouchControls}
              data-testid="settings-touch-controls"
              aria-label="Virtual Touch Controls"
            />
          </div>
        </div>
      </section>

      <section className="settings-section" aria-labelledby="settings-keybinds-heading">
        <h3 className="settings-section-title" id="settings-keybinds-heading">Keyboard & Controller Bindings</h3>
        <div className="settings-card">
          <div className="settings-card-row">
            <div>
              <div className="settings-card-label">Key Bindings</div>
              <div className="settings-hint" style={{ margin: '2px 0 0' }}>
                Rebind movement, combat, action bars, and window hotkeys with a live visual keyboard map.
              </div>
            </div>
            <TomeButton
              tone="primary"
              data-testid="open-key-bindings-button"
              onClick={() => keyBindingsNavigationStore.openFromSettings()}
            >
              Open Key Bindings
            </TomeButton>
          </div>
        </div>
      </section>
    </>
  );
};
